import React, { useMemo } from 'react';
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatDuration, formatNumber, formatPercent } from '../../../utils/format';
import { trendsFor } from '../../../utils/userActivity';
import type { Agent, UserTrendPoint, VoiceSource } from '../../../types/voip';
type TrendKey = Exclude<keyof UserTrendPoint, 'label'>;
const series: {
  key: TrendKey;
  label: string;
  format: (v: number) => string;
  higherIsBetter: boolean;
}[] = [{
  key: 'talkSeconds',
  label: 'Talk time',
  format: formatDuration,
  higherIsBetter: true
}, {
  key: 'calls',
  label: 'Call volume',
  format: formatNumber,
  higherIsBetter: true
}, {
  key: 'answerRate',
  label: 'Answer rate',
  format: formatPercent,
  higherIsBetter: true
}, {
  key: 'avgSeconds',
  label: 'Avg call duration',
  format: formatDuration,
  higherIsBetter: true
}];
interface DrawerTrendsProps {
  agent: Agent;
  source: VoiceSource;
}
export function DrawerTrends({
  agent,
  source
}: DrawerTrendsProps) {
  const data = useMemo(() => trendsFor(agent, source), [agent, source]);
  const last = data[data.length - 1];
  const prev = data[data.length - 2];
  return <section className="p-6" aria-labelledby="drawer-trends-heading" data-id="element-605" data-mp="1cz60no4ryxkr">
      <h3 id="drawer-trends-heading" className="text-sm font-semibold text-ink" data-id="element-606" data-mp="z17n2kya356b">
        Weekly trends
      </h3>
      <p className="text-xs text-muted" data-id="element-607" data-mp="135fh8xni9dbx">Last 8 weeks, change vs previous week</p>

      <ul className="mt-4 divide-y divide-line" data-id="element-608" data-mp="pad3xpgbwlp3">
        {series.map(s => {
        const change = s.key === 'answerRate' ? last[s.key] - prev[s.key] : prev[s.key] === 0 ? 0 : (last[s.key] - prev[s.key]) / prev[s.key];
        const good = s.higherIsBetter ? change >= 0 : change <= 0;
        const changeLabel = s.key === 'answerRate' ? `${change >= 0 ? '+' : '−'}${Math.abs(Math.round(change * 1000) / 10)} pts` : `${change >= 0 ? '+' : '−'}${Math.abs(Math.round(change * 1000) / 10)}%`;
        return <li key={s.key} className="py-4 first:pt-0 last:pb-0" data-id="element-609" data-mp="2d1vmoqio0u8g">
              <div className="flex items-baseline justify-between gap-3" data-id="element-610" data-mp="2d36qgqc421zk">
                <p className="text-xs font-medium text-muted" data-id="element-611" data-mp="289b0ob603lfh">{s.label}</p>
                <p className="flex items-baseline gap-2" data-id="element-612" data-mp="23cc6jsmc7eha">
                  <span className="text-base font-bold text-ink" data-id="element-613" data-mp="1za7f90jd2txt">{s.format(last[s.key])}</span>
                  <span className={`text-xs font-semibold ${good ? 'text-positive' : 'text-danger'}`} data-id="element-614" data-mp="2w07y6ix1ug77">{changeLabel}</span>
                </p>
              </div>
              <div className="mt-2 h-16" data-id="element-615" data-mp="2r1myqlpmg0jt">
                <ResponsiveContainer width="100%" height="100%" data-id="element-616" data-mp="2mz9uk1qiwap5">
                  <LineChart data={data} margin={{
                top: 4,
                right: 4,
                left: 4,
                bottom: 0
              }} data-id="element-617" data-mp="2mxp2cirol3ug">
                    <XAxis dataKey="label" hide data-id="element-618" data-mp="2i05mxfj8yl0x" />
                    <YAxis hide domain={['dataMin', 'dataMax']} data-id="element-619" data-mp="1bkwal7yw4fee" />
                    <Tooltip cursor={{
                  stroke: '#e4e9ef'
                }} contentStyle={{
                  borderRadius: 10,
                  border: '1px solid #e4e9ef',
                  fontSize: 12
                }} formatter={v => [s.format(Number(v)), s.label]} data-id="element-620" data-mp="1bi0bvhf7xmow" />
                    <Line type="monotone" dataKey={s.key} stroke="#00364d" strokeWidth={2} dot={false} activeDot={{
                  r: 3,
                  fill: '#2563eb',
                  stroke: '#fff'
                }} animationDuration={300} data-id="element-621" data-mp="3n889mlmooxm5" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </li>;
      })}
      </ul>
      <div className="mt-2 flex justify-between text-[11px] text-subtle" data-id="element-622" data-mp="et7x4zzxfxjj">
        <span data-id="element-623" data-mp="itry6enmflay">{data[0].label}</span>
        <span data-id="element-624" data-mp="isp7a6qativ1">{last.label}</span>
      </div>
    </section>;
}